import Vue from 'vue';
import Moment from 'moment-timezone';
import XLSX from 'xlsx';
import { setColumnSize } from '@/utils/sheets';
import { i18n } from '@/plugins/i18n';

/**
 * Available types of checklist items
 */
export const GW_CHECKLIST_ITEM_TYPE = {
  OKNOK: 0,
  GW_INDICATOR: 1,
  DMP_INDICATOR: 2,
};

/**
 * Create an excel sheet for a finished gemba walk
 * @param  {Array} gw    Current exported finished gemba walk
 */
export function exportGwToExcel(gw, companyUsers) {
  const dateFormat = i18n.t('dateFormat');

  // Initializing the empty data of the steps of the GW
  const steps = [];

  // Preparing the summary count for OK/Total
  let summaryOkCount = 0;
  let summaryNokCount = 0;
  let summaryNACount = 0;
  let summaryTotalCount = 0;

  summaryTotalCount += gw.steps.length || 0;
  gw.steps.forEach((step, length) => {
    if (step.ok && step.ok === 1) {
      summaryOkCount += 1;
    } else if (step.ok === 0) {
      summaryNokCount += 1;
    } else if (step.ok && step.ok === 2) {
      summaryNACount += 1;
    }
    // Initializing the comments list for the step item
    const stepCommentsList = [];
    if (step.comments && step.comments.length) {
      stepCommentsList.push(i18n.t('message.COMMENTS'));
      step.comments.forEach((comment) => {
        stepCommentsList.push(comment.text.replace(/<\/?[^>]+(>|$)/g, ''));
      });
    }
    // Adding the step infos into steps array
    steps.push(
      [],
      [`${i18n.t('message.STEP')} ${length + 1}`, step.name],
      [i18n.t('message.OBJECTIVE'), step.objective],
      [i18n.t('message.PROBLEMS'), step.problems.length],
      [
        i18n.t('message.STEP_VALIDATION'),
        (
          (step.ok === 1 && i18n.t('message.YES'))
          || (step.ok === 2 && i18n.t('message.NOT_APPLICABLE_SHORT'))
          || (step.ok === 0 && i18n.t('message.NO'))
        ),
      ],
    );

    if (step.auditedUser && companyUsers[`${step.auditedUser}`]) {
      steps.push([i18n.t('message.GW_AUDITED_USER'), i18n.t('fullNameFormat', companyUsers[`${step.auditedUser}`])]);
    }

    if (stepCommentsList.length) {
      steps.push(stepCommentsList);
    }

    if (step.checklists) {
      // Checking the checklists inside a step item
      step.checklists.forEach((checklist) => {
        steps.push(
          [],
          [i18n.t('message.GW_CHECKLIST'), checklist.name],
          [],
        );

        if (checklist.items && checklist.items.length) {
          steps.push(
            [
              '',
              i18n.t('message.NAME'),
              i18n.t('message.GW_CL_ITEM_TYPE_OKNOK'),
              i18n.t('message.VALUE'),
              i18n.t('message.OBJECTIVE'),
              i18n.t('message.PROBLEMS'),
              i18n.t('message.COMMENTS'),
            ],
          );
          summaryTotalCount += checklist.items.length;
          checklist.items.forEach((item) => {
            if (item.ok && item.ok === 1) {
              summaryOkCount += 1;
            } else if (item.ok === 0) {
              summaryNokCount += 1;
            } else if (item.ok && item.ok === 2) {
              summaryNACount += 1;
            }
            const itemCommentsList = [];
            if (item.comments && item.comments.length) {
              item.comments.forEach((comment) => {
                itemCommentsList.push([comment.text.replace(/<\/?[^>]+(>|$)/g, '')]);
              });
            }
            const stepsMainInformations = [
              '',
              item.text,
              (
                (item.ok === 1 && i18n.t('message.YES'))
                || (item.ok === 2 && i18n.t('message.NOT_APPLICABLE_SHORT'))
                || (item.ok === 0 && i18n.t('message.NO'))
              ),
              item.value ? `${item.value} (${i18n.t('message.INDICATOR')})` : '',
              item.objective,
              item.problems.length,
            ];
            const stepsElements = stepsMainInformations.concat(itemCommentsList);
            steps.push(
              stepsElements,
            );
          });
        }
      });
    }
  });

  // Main datas about the Gemba Walk
  const gwData = [
    [i18n.t('message.NAME'), gw.name],
    [i18n.t('message.OBJECTIVE'), gw.objective],
    [i18n.t('message.CREATION_DATE'), gw.date],
    [i18n.t('message.PARTICIPANTS'), String(gw.participants)],
    [],
    ['OK', 'NOK', 'N/A', i18n.t('message.SUMMARY')],
    [summaryOkCount, summaryNokCount, summaryNACount, `${summaryOkCount} / ${summaryTotalCount}`],
    [],
  ];
  // We're linking the steps data with the main datas.
  const fullData = gwData.concat(steps);

  const now = Vue.moment();
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: i18n.t('message.GW'),
    Subject: gw.name,
    CreateDate: Vue.moment().format(dateFormat),
  };

  const usersSheet = XLSX.utils.aoa_to_sheet(fullData);
  setColumnSize(usersSheet, 25);
  XLSX.utils.book_append_sheet(wb, usersSheet, i18n.t('message.GW'));
  const fileName = `Shizen_${i18n.t('message.GW')}_${gw.name}_${now.format(dateFormat)}.xlsx`;
  // Removing spaces in file name before creating it
  XLSX.writeFile(wb, fileName.replace(/\s/g, ''));
}

/** Array: All GW checks related to this team */
export function getAllGwChecks(gw, team) {
  if (!gw) {
    return [];
  }
  const teamSteps = gw.steps.filter(s => s.auditedTeam === team.id);
  return teamSteps.reduce((allChecks, step) => {
    const items = step.checklists.reduce((allItems, checklist) => [...allItems, ...checklist.items], []);
    return [...allChecks, ...items];
  }, []);
}

/**
 * Get the information item summary of a passed field tour.
 * @param {Object} gw - Field tour object
 * @param {Object} team - Recipient for the summary
 * @return {String} Summary
 */
export function getGWInfoSummary(gw, team, additionalText = '') {
  const allGwChecks = getAllGwChecks(gw, team);
  const nNok = allGwChecks.filter(check => check.ok === 0 || check.okValue === 0).length;
  const nOk = allGwChecks.filter(check => check.ok === 1 || check.okValue === 1).length;
  const nItems = nNok + nOk;
  const date = Moment(gw.date).format(i18n.t('dateDayFormat'));
  const { name } = gw;

  return i18n.t('message.GW_TEAM_SUMMARY_INTRO', { name, date })
    + i18n.tc('message.GW_TEAM_SUMMARY_CHECKS', nItems, { nItems, nOk, nNok })
    + (additionalText.length ? `<br>${additionalText}` : '');
}

export default {
  GW_CHECKLIST_ITEM_TYPE,
  exportGwToExcel,
  getGWInfoSummary,
  getAllGwChecks,
};
